// Пароль для входа в настройках: задан ли, работает ли код из Telegram, смена по текущему паролю.
import { Button, Chip, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { LockKeyhole } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';

interface PasswordState {
  set: boolean;
  updatedAt: string | null;
  /** Бот привязан — при входе к паролю добавляется код из Telegram. */
  telegram: boolean;
  /** Коды приходят в группу (их видят все участники) — что сделать, чтобы они шли только владельцу. */
  codesWarning?: string | null;
}

export function PasswordBlock() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['password'], queryFn: () => api<PasswordState>('/api/auth/password') });
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const s = q.data;

  const save = async () => {
    setError(null);
    if (next !== repeat) return setError('Пароли не совпадают');
    setBusy(true);
    try {
      await api('/api/auth/password', { current: current || undefined, password: next });
      toast(s?.set ? 'Пароль изменён' : 'Пароль задан');
      setOpen(false);
      setCurrent('');
      setNext('');
      setRepeat('');
      void qc.invalidateQueries({ queryKey: ['password'] });
      void qc.invalidateQueries({ queryKey: ['auth'] });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pw-block">
      <div className="flex items-start gap-3">
        <LockKeyhole size={18} className="muted" style={{ marginTop: 2 }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <b style={{ fontWeight: 500 }}>Пароль</b>
            {s?.set ? (
              s.telegram ? (
                <Chip tone="success" dot>
                  с кодом из Telegram
                </Chip>
              ) : (
                <Chip tone="warning">без второго шага</Chip>
              )
            ) : (
              <Chip>не задан</Chip>
            )}
          </div>
          <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
            {s?.set
              ? `изменён ${s.updatedAt ? when(s.updatedAt) : 'давно'}`
              : 'Вход по паролю и коду из Telegram — удобнее ключа на новом устройстве.'}
          </div>
          {s?.set && !s.telegram && (
            <p className="pw-warn">
              Бот Telegram ещё не привязан, поэтому при входе код не спрашивается.{' '}
              <Link to="/settings" search={{ tab: 'integrations' }} hash="telegram">
                Подключить Telegram
              </Link>
            </p>
          )}
          {s?.set && s.telegram && s.codesWarning && <p className="pw-warn">{s.codesWarning}</p>}
        </div>
        {!open && (
          <Button size="sm" onClick={() => setOpen(true)}>
            {s?.set ? 'Сменить' : 'Задать'}
          </Button>
        )}
      </div>
      {open && (
        <form
          className="token-form"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          {s?.set && (
            <div>
              <label className="label" htmlFor="pw-current">
                Текущий пароль
              </label>
              <input
                id="pw-current"
                className="input"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
            </div>
          )}
          <div>
            <label className="label" htmlFor="pw-next">
              Новый пароль
            </label>
            <input
              id="pw-next"
              className="input"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            <p className="auth-hint">Не короче 12 символов. Удобнее всего фраза из 3–4 слов.</p>
          </div>
          <div>
            <label className="label" htmlFor="pw-repeat">
              Новый пароль ещё раз
            </label>
            <input
              id="pw-repeat"
              className="input"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          </div>
          {error && (
            <p className="notice error" role="alert" style={{ margin: 0 }}>
              {error}
            </p>
          )}
          <div className="row">
            <Button
              type="submit"
              variant="primary"
              disabled={busy || next.length < 12 || (s?.set && !current)}
            >
              {busy ? 'Сохраняю…' : 'Сохранить пароль'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
            >
              Отмена
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
